/**
 * Compress document attachment images before upload to stay under server/nginx limits.
 */

import { isSupportedImageUploadFile, wrapUploadFileWithMime } from './attachmentHelpers';
import { ensureJpegUploadFile } from './heicConverter';

export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
export const UPLOAD_MAX_DIMENSION_DEFAULT = 2200;
export const UPLOAD_JPEG_QUALITY_DEFAULT = 0.85;
export const UPLOAD_RETRY_MAX_DIMENSION = 1800;
export const UPLOAD_RETRY_JPEG_QUALITY = 0.75;

export const UPLOAD_COMPRESSION_STANDARD = {
  maxDimension: UPLOAD_MAX_DIMENSION_DEFAULT,
  quality: UPLOAD_JPEG_QUALITY_DEFAULT,
};

export const UPLOAD_COMPRESSION_AGGRESSIVE = {
  maxDimension: UPLOAD_RETRY_MAX_DIMENSION,
  quality: UPLOAD_RETRY_JPEG_QUALITY,
};

export function formatByteSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return 'unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function isImageUploadFile(file) {
  return isSupportedImageUploadFile(file);
}

export function isPayloadTooLargeError(error) {
  const status = error?.response?.status;
  if (status === 413) return true;
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && /too large|entity too large|413/i.test(detail)) return true;
  if (typeof error?.message === 'string' && /413|entity too large|too large/i.test(error.message)) {
    return true;
  }
  return false;
}

/** True for network blips and 5xx responses where a immediate retry may succeed. */
export function isTransientUploadError(error) {
  if (!error) return false;
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') return true;
  const status = error?.response?.status;
  if (!status) return true;
  return status >= 500 && status <= 599;
}

export function messageForUploadError(error, { retriedCompression = false } = {}) {
  if (isPayloadTooLargeError(error)) {
    if (retriedCompression) {
      return 'Upload failed because the file is too large. Please upload a smaller image or PDF.';
    }
    return 'This file is too large. We compressed it automatically — please try again.';
  }
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return null;
}

export function logUploadPayloadSizes(context, files) {
  const payload = {};
  Object.entries(files || {}).forEach(([key, file]) => {
    if (!file) {
      payload[key] = null;
      return;
    }
    payload[key] = {
      name: file.name,
      type: file.type,
      bytes: file.size,
      size: formatByteSize(file.size),
    };
  });
  console.info('[ATTACH UPLOAD] payload sizes', context, payload);
}

function loadImageElementFromFile(file) {
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };
    img.src = url;
  });
}

function jpegFilenameFrom(file) {
  const base = String(file?.name || 'document').replace(/\.[^/.]+$/, '');
  return `${base}.jpg`;
}

/**
 * Resize and re-encode an image File as JPEG for upload.
 * PDF and non-image files pass through unchanged.
 */
export async function compressImageFileForUpload(
  file,
  { maxDimension = UPLOAD_MAX_DIMENSION_DEFAULT, quality = UPLOAD_JPEG_QUALITY_DEFAULT } = {},
) {
  if (!file || !isImageUploadFile(file)) {
    return file;
  }

  const normalizedFile = wrapUploadFileWithMime(file);
  const img = await loadImageElementFromFile(normalizedFile);
  let { width, height } = img;
  const maxSide = Math.max(width, height);
  if (maxSide > maxDimension) {
    const scale = maxDimension / maxSide;
    width = Math.max(1, Math.round(width * scale));
    height = Math.max(1, Math.round(height * scale));
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('JPEG compression failed'))),
      'image/jpeg',
      quality,
    );
  });

  return new File([blob], jpegFilenameFrom(file), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Prepare a single upload payload: HEIC-safe JPEG + dimension/quality compression.
 */
export async function prepareImageUploadPayload(file, compressionProfile = UPLOAD_COMPRESSION_STANDARD) {
  if (!file) return null;
  if (!isImageUploadFile(file)) return file;
  const normalizedFile = wrapUploadFileWithMime(file);
  const jpegFile = await ensureJpegUploadFile(normalizedFile);
  return compressImageFileForUpload(jpegFile, compressionProfile);
}
